/**
 * FOS Results List Component
 *
 * Displays search results as a refined list of decision cards with:
 * - Reference, outcome, company, product
 * - Core issue summary
 * - Compensation (if awarded)
 * - Similarity score (for semantic search)
 * - Pagination controls
 *
 * Uses GO brand colors via OutcomeBadge:
 * - Berry → Upheld (risk to firm)
 * - Teal → Not Upheld (favorable)
 * - Purple → Partially Upheld (needs review)
 */

import React from 'react';
import type { FOSDecisionCard as FOSDecisionCardData } from '../../../utils/api';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { FOSDecisionCard } from './FOSDecisionCard';
import IconWrapper from '../../IconWrapper';
import { FiChevronLeft, FiChevronRight, FiFileText } from 'react-icons/fi';
import { cn } from '../../../lib/utils';

interface FOSResultsListProps {
  decisions: FOSDecisionCardData[];
  isLoading: boolean;
  onDecisionClick: (decision: FOSDecisionCardData) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const LoadingSkeleton: React.FC = () => (
  <div className="rounded-lg border border-border bg-card shadow-[var(--shadow-card)]">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="border-b border-border/50 p-4 last:border-b-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2.5">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-5 w-14 rounded-full" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
          <Skeleton className="h-7 w-16 rounded-md" />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card py-16 shadow-[var(--shadow-card)]">
    <div className="flex items-center justify-center w-14 h-14 rounded-full bg-muted/50 mb-4">
      <IconWrapper icon={FiFileText} className="h-6 w-6 text-muted-foreground/50" />
    </div>
    <p className="text-base font-medium text-foreground mb-1">No decisions found</p>
    <p className="text-sm text-muted-foreground">Try adjusting your search or filters</p>
  </div>
);

const FOSResultsList: React.FC<FOSResultsListProps> = ({
  decisions,
  isLoading,
  onDecisionClick,
  page,
  totalPages,
  onPageChange,
}) => {
  if (isLoading && decisions.length === 0) {
    return <LoadingSkeleton />;
  }

  if (decisions.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden rounded-lg border border-border bg-card shadow-[var(--shadow-card)]">
        <div className="flex-1 divide-y divide-border/60 overflow-hidden">
          {decisions.map((decision) => (
            <FOSDecisionCard
              key={decision.id}
              decision={decision}
              variant="list"
              onClick={() => onDecisionClick(decision)}
            />
          ))}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-3 flex-shrink-0 flex items-center justify-center gap-1 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 0}
            className="gap-1 text-muted-foreground hover:text-foreground"
          >
            <IconWrapper icon={FiChevronLeft} className="h-4 w-4" />
            <span className="hidden sm:inline">Previous</span>
          </Button>

          <div className="flex items-center gap-0.5 px-2">
            {/* Show page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              // Calculate which pages to show
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i;
              } else if (page < 3) {
                pageNum = i;
              } else if (page > totalPages - 4) {
                pageNum = totalPages - 5 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? 'default' : 'ghost'}
                  size="sm"
                  className={cn(
                    "w-8 h-8 p-0 tabular-nums",
                    page === pageNum
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum + 1}
                </Button>
              );
            })}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
            className="gap-1 text-muted-foreground hover:text-foreground"
          >
            <span className="hidden sm:inline">Next</span>
            <IconWrapper icon={FiChevronRight} className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default FOSResultsList;
